import React, { useState, useRef, useEffect } from 'react';
import { TextField, Paper, List, ListItem, ListItemText, ClickAwayListener, Box, InputAdornment, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useTheme } from '@mui/material/styles';

/**
 * Custom Autocomplete component that works reliably in Android WebView
 */
const CustomAddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  options,
  label,
  required,
  error,
  helperText,
  placeholder,
  fullWidth = true,
  sx = {}
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const containerRef = useRef(null);
  const theme = useTheme(); // Access the MUI theme
  const isDarkMode = theme.palette.mode === 'dark';

  // Update internal state when external value changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Handle scrolling behavior - prevent body scroll when dropdown is open
  useEffect(() => {
    const handleScroll = (e) => {
      if (open && menuRef.current && !menuRef.current.contains(e.target)) {
        e.preventDefault();
      }
    };

    if (open) {
      document.addEventListener('touchmove', handleScroll, { passive: false });
    }

    return () => {
      document.removeEventListener('touchmove', handleScroll);
    };
  }, [open]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
    // Only show options if we have text
    if (newValue.trim().length > 0) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleItemClick = (option) => {
    // Close the dropdown immediately and forcefully
    setOpen(false);
    
    // Set the input value to the selected option
    if (typeof option === 'string') {
      setInputValue(option);
    } else {
      setInputValue(option.description);
    }
    
    // Call the onSelect callback with a slight delay to ensure state updates
    setTimeout(() => {
      if (onSelect) {
        onSelect(option);
      }
    }, 0);
    
    // Focus back on input after selection for better mobile experience
    setTimeout(() => {
      inputRef.current?.blur(); // First blur to ensure any native focus handling completes
      setTimeout(() => {
        inputRef.current?.focus(); // Then refocus
        // Ensure the dropdown stays closed
        setOpen(false);
      }, 50);
    }, 10);
  };

  const handleClear = () => {
    setInputValue('');
    if (onChange) {
      onChange('');
    }
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box ref={containerRef} sx={{ position: 'relative', ...sx }}>
        <TextField
          inputRef={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => options && options.length > 0 && inputValue.trim().length > 0 && setOpen(true)}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
          fullWidth={fullWidth}
          size="small"
          InputProps={{
            endAdornment: inputValue ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear input"
                  onClick={handleClear}
                  edge="end"
                  size="small"
                  sx={{
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : undefined
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDarkMode ? 'rgba(50, 50, 50, 0.8)' : '#f8fafc',
              '& fieldset': {
                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.23)' : undefined,
              },
              '&:hover fieldset': {
                borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : undefined,
              },
              '&.Mui-focused fieldset': {
                borderColor: isDarkMode ? 'primary.main' : undefined,
              }
            },
            '& .MuiInputBase-input': {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.9)' : undefined,
              '&::placeholder': {
                color: isDarkMode ? 'rgba(255, 255, 255, 0.5)' : undefined,
                opacity: 1
              }
            },
            '& .MuiInputLabel-root': {
              color: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : undefined,
              '&.Mui-focused': {
                color: isDarkMode ? 'primary.main' : undefined
              }
            }
          }}
        />

        {open && options && options.length > 0 && (
          <Paper
            ref={menuRef}
            elevation={4}
            sx={{
              position: 'absolute',
              zIndex: 1000,
              maxHeight: '200px',
              overflowY: 'auto',
              width: '100%', // Match parent width exactly
              top: '100%',
              left: 0,
              mt: 0.5,
              borderRadius: '4px',
              backgroundColor: isDarkMode ? 'rgba(45, 45, 45, 0.98)' : '#ffffff',
              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.15)' : undefined,
            }}
          >
            <List disablePadding>
              {options.map((option, index) => {
                const optionText = typeof option === 'string' ? option : option.description;
                return (
                  <ListItem
                    key={index}
                    button
                    dense
                    onClick={() => handleItemClick(option)}
                    sx={{
                      py: 1.5,
                      color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : undefined,
                      '&:hover': {
                        backgroundColor: isDarkMode ? 'rgba(90, 90, 90, 0.6)' : 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ListItemText 
                      primary={optionText}
                      primaryTypographyProps={{
                        sx: { color: isDarkMode ? 'rgba(255, 255, 255, 0.87)' : undefined }
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default CustomAddressAutocomplete; 